import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BookOpen, Users, Target, MonitorSmartphone, Award, MapPin, Phone, CheckCircle } from "lucide-react";

const ABOUT_IMAGE = "https://images.pexels.com/photos/7616700/pexels-photo-7616700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CLASSROOM_IMAGE = "/about/our-approach.webp";

const highlights = [
  "360 Hybrid Doubt Support - Offline + Online",
  "Upto Selection Mentorship - Prelims to Interview",
  "AC Classrooms with Digital Boards",
  "Vedic Math & Shortcut Techniques",
  "Updated 2025-26 Exam Patterns",
  "In-House Library & Study Space",
  "Regular Mock Tests & Analysis",
  "Prime Location near Badlapur Station",
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <Helmet>
        <title>About Vedanti Academy — Faculty & Method</title>
        <meta name="description" content="Learn about Vedanti Academy's mission, expert faculty, AC classrooms with digital boards, 360-degree hybrid doubt support, and our proven track record in Banking, SSC, MBA & Railway exam coaching." />
        <meta name="keywords" content="about Vedanti Academy, coaching institute Badlapur, expert faculty, teaching methodology, exam coaching centre" />
        <meta property="og:title" content="About Vedanti Academy - Expert Coaching Since Day One" />
        <meta property="og:description" content="Discover our story, teaching methodology, and why 500+ students rate us 5 stars on Google." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantiacademy.com/about" />
        <link rel="canonical" href="https://vedantiacademy.com/about" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Vedanti Academy",
          "description": "Learn about Vedanti Academy's mission, expert faculty, and teaching methodology for competitive exam coaching.",
          "url": "https://vedantiacademy.com/about",
          "mainEntity": {"@type": "EducationalOrganization", "name": "Vedanti Academy", "url": "https://vedantiacademy.com", "foundingLocation": "Badlapur, Maharashtra"}
        })}</script>
      </Helmet>
      {/* Breadcrumb Hero */}
      <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main relative z-10">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">About <span className="text-vedanti-orange">Vedanti Academy</span></h1>
          <p className="text-slate-300 mt-3 text-lg">Most trusted coaching classes in Badlapur, Ambernath, Kalyan & surrounding areas</p>
        </div>
      </section>

      {/* About Content */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-vedanti-orange font-semibold text-sm uppercase tracking-wider">Who We Are</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-vedanti-navy mt-2 mb-6">
                Transforming Aspirants into Achievers Since 2017
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Welcome to <strong className="text-vedanti-navy">Vedanti Academy</strong>, the most trusted coaching classes in Badlapur, Ambernath, Ulhasnagar, Kalyan, Dombivli, Karjat, and Mumbai. We provide the best coaching for Banking exams (IBPS PO/Clerk, SBI PO/Clerk, RBI), MBA entrance (MBA CET, CAT, CMAT, SNAP), SSC exams (CGL, CHSL), Railway exams (RRB NTPC, Group D), Insurance exams, and other Government & Competitive exams from our branch in Badlapur.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                We are top-rated coaching classes with the best faculties, mock tests, study materials, doubt solving, flexible batches, digital boards, AC classrooms and library. Our commitment doesn&apos;t end when the syllabus finishes - we provide &quot;Upto Selection Support&quot; through Prelims, Mains, and Interview stages.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-vedanti-orange flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-600">{h}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/demo-class"
                className="bg-vedanti-orange text-white hover:bg-vedanti-orange/90 rounded-lg px-6 py-3 font-semibold inline-flex items-center gap-2 transition-all shadow-lg shadow-orange-500/20"
                data-testid="about-demo-btn"
              >
                Attend Free Demo Lecture
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-3 bg-vedanti-mango/10 rounded-2xl -rotate-2"></div>
              <img src={ABOUT_IMAGE} alt="Vedanti Academy students studying outdoors" className="relative rounded-2xl shadow-xl w-full h-[480px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img src={CLASSROOM_IMAGE} alt="Vedanti Academy students with their certificates — proud achievers of our coaching programs" className="rounded-2xl shadow-xl w-full h-[400px] object-cover" />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-vedanti-orange font-semibold text-sm uppercase tracking-wider">Our Approach</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-vedanti-navy mt-2 mb-6">
                Student-First & Result-Oriented
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We are not a &quot;coaching factory.&quot; We prioritize individual growth over batch size. Our approach includes personalized attention with regular progress tracking for every student.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                We focus on <strong className="text-vedanti-navy">Shortcuts, Vedic Math, and Time-Management tricks</strong> designed specifically to clear high-pressure cut-offs. Our dynamic material and test series are updated in real-time to reflect the latest changes in IBPS, SSC, and CET patterns.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                Moving beyond rote learning, our logic-based coaching focuses on the practical application required by modern exams. Regular motivational counseling sessions keep your morale high during the long preparation journey.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/consultancy" className="bg-vedanti-navy text-white hover:bg-vedanti-navy/90 rounded-lg px-6 py-3 font-semibold transition-all" data-testid="about-consultancy-btn">
                  Book Consultancy
                </Link>
                <a href="tel:+918308776505" className="border-2 border-vedanti-navy text-vedanti-navy hover:bg-vedanti-navy hover:text-white rounded-lg px-6 py-3 font-medium transition-colors inline-flex items-center gap-2" data-testid="about-call-btn">
                  <Phone size={18} /> Call Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Branches */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="text-vedanti-orange font-semibold text-sm uppercase tracking-wider">Our Branches</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-vedanti-navy mt-2">Visit Us</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-vedanti-navy rounded-xl flex items-center justify-center">
                  <MapPin size={24} className="text-white" />
                </div>
                <h3 className="font-heading font-bold text-xl text-vedanti-navy">Badlapur Branch (Main)</h3>
              </div>
              <p className="text-slate-600 mb-3">3rd Floor, Jadhav Market, above Aradhana Hotel, Badlapur, Maharashtra 421503</p>
              <a href="tel:+918308776505" className="text-vedanti-orange font-semibold flex items-center gap-2">
                <Phone size={16} /> +91 8308776505
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
